/**
 * @typedef {'toilet' | 'sink' | 'shower' | 'bathtub' | 'tub-shower'} FixtureType
 * User-selectable fixture types.
 * 'tub-shower' is only valid in a qualifying 60" alcove (see isAlcoveWall).
 */

/**
 * @typedef {'double-sink'} AutoCreatedFixtureType
 * Fixtures created automatically during pipeline
 * Note: Corner showers are now represented as shower with wallCount=2
 */

/**
 * @typedef {Object} WallAssignment
 * @property {FixtureType} fixtureType - Type of fixture
 * @property {string} wallId - ID of wall to place fixture on
 */

/**
 * @typedef {Object} WallAssignmentSet
 * @property {WallAssignment[]} assignments - Array of fixture-to-wall assignments
 * @property {string} key - Unique key for this assignment set
 */

/**
 * @typedef {Object} Wall
 * @property {string} id - Wall identifier
 * @property {{x: number, z: number}} start - Start point
 * @property {{x: number, z: number}} end - End point
 * @property {number} length - Wall length in meters
 * @property {{x: number, z: number}} normal - Normal vector pointing into room
 */

/**
 * @typedef {Object} Bathroom
 * @property {string} id - Bathroom identifier
 * @property {{x: number, z: number}[]} boundary - Room boundary corners (counter-clockwise)
 * @property {Wall[]} walls - Array of walls
 * @property {Object[]} doors - Array of doors
 * @property {Object[]} windows - Array of windows
 * @property {Object[]} [lockedFixtures] - Existing fixtures that cannot be moved (additive mode)
 */

// Default physical dimensions per fixture type (in meters)
const {
  computeClearanceDimensions,
} = require('../../collision/clearanceConfig');
// The vanity a generated sink is actually BUILT as, so the pipeline's notion
// of a sink's normal depth cannot drift from the assembly's.
const { VANITY_DIMENSIONS } = require('../../vanity-assembly/constants');

const FIXTURE_PHYSICAL = {
  toilet: { width: 0.508, depth: 0.762 }, // 20" x 30"
  sink: { width: 0.61, depth: 0.381 }, // 24" x 15"
  shower: { width: 0.762, depth: 0.762 }, // 30" x 30"
  // Bathtub fixture = surround footprint. Placement code sizes a tub
  // model to fit inside this rectangle.
  bathtub: { width: 1.524, depth: 0.762 }, // 60" x 30"
  'tub-shower': { width: 1.524, depth: 0.762 }, // 60" x 30" — width fixed by alcove; depth may expand to 36"
  'double-sink': { width: 1.22, depth: 0.508 }, // 48" x 20"
};

/**
 * The depth at which a fixture type is NORMALLY BUILT — the size stage 6 may
 * grow a fixture back to without having to earn it against the score gate.
 *
 * Distinct from `FIXTURE_PHYSICAL[type].depth`, which is the depth the
 * PLACEMENT stages seed a fixture at. For most types the two are the same
 * number, and this table just restates it. For a sink they are not, and
 * conflating them is what left generated vanities unbuildable:
 *
 *   - the placement seed is 15" — deliberately shallow, so a sink can be
 *     tried on walls a real vanity would not fit against;
 *   - a vanity is actually built at 21" of carcass (`VANITY_DIMENSIONS`),
 *     which `buildVanityCountertop` finishes at 22" with its 1" front
 *     overhang;
 *   - and below 15.31" (`WALL_HUNG_SINK_MAX.depth`) there is no catalog
 *     basin that fits the box at all, so `assembleVanity` stops building a
 *     vanity and returns a wall-hung basin instead.
 *
 * So the seed sits 0.31" INSIDE the band where the thing cannot be built,
 * and every inch out of that band was being charged to the score gate.
 * The gate compares walkable area lost against `getPhysicalExpansionBonus`,
 * an atan curve on the area ratio — the cost is linear in the fixture's
 * width while the benefit saturates, so a sink that lateral expansion has
 * already run out to a long wall can never buy its own depth. Measured on
 * MASTER bathroom / A69C34AD: the top-ranked layout grew its sink to 93"
 * wide and was then refused all 7" of depth for a net −0.065, delivering a
 * 93" x 15" box that came out of assembly as a 24" wall-hung basin. The
 * fourth-ranked layout, 65" wide on the same wall, cleared the same gate at
 * +0.110 and got its 21".
 *
 * Growth up to this depth is restoration, not a spaciousness trade, and is
 * exempt from the score gate. It is NOT exempt from anything else: every
 * amount is still validated for collisions, clearances and
 * `allFixturesAccessible`, so a fixture with no room simply does not move.
 * Growth BEYOND this depth stays gated.
 */
const RESTORATION_DEPTHS = {
  ...Object.fromEntries(
    Object.entries(FIXTURE_PHYSICAL).map(([type, dims]) => [type, dims.depth])
  ),
  sink: VANITY_DIMENSIONS.singleSink.depth,
  'double-sink': VANITY_DIMENSIONS.doubleSink.depth,
};

// Lower bounds per fixture type. Stage 5 depth-shrink collision resolution
// must not push a fixture below these; if it would, the pipeline should
// reject the layout rather than silently produce a 2"-deep shower.
// SINGLE SOURCE OF TRUTH for fixture minima: the web-app's
// MINIMUM_FIXTURE_DIMENSIONS (bathroomFixtureConstants.js) is derived from
// this table — never define a parallel copy.
const MIN_PHYSICAL_DIMENSIONS = {
  toilet: { width: 0.508, depth: 0.6223 }, // 20" x 24.5"
  // Sink shrinks down to 14" x 10" — the floor that used to live on
  // small-sink. Lets S5 depth-shrink resolve collisions without the
  // small-sink retry pass swapping fixture types.
  sink: { width: 0.3556, depth: 0.254 }, // 14" x 10"
  // 28" x 28": the nominal-30" stalls of older houses measure 28-29"
  // inside the tile; refusing them loses real bathrooms. 30" x 30" stays
  // the DEFAULT (FIXTURE_PHYSICAL) — this only kicks in under S5 shrink.
  shower: { width: 0.7112, depth: 0.7112 }, // 28" x 28"
  // Smallest tub: 46" x 27" (Bootz Honolulu, the standard compact alcove).
  bathtub: { width: 1.1684, depth: 0.6858 }, // 46" x 27"
  // 46.5" x 28" — the narrowest alcove tub the catalog stocks, and the
  // shallowest. The width floor was 60" while the gate only admitted a 60"
  // recess; now the recess sets the width (`baseDims`), so the floor has to
  // reach the narrowest tub that can actually fill one (#1865).
  'tub-shower': { width: 1.1811, depth: 0.711 },
  'double-sink': { width: 1.2192, depth: 0.381 }, // 48" x 15"
};

// Fixture dimensions derived from physical sizes + clearance rules
const FIXTURE_DIMENSIONS = {};
for (const [type, physical] of Object.entries(FIXTURE_PHYSICAL)) {
  FIXTURE_DIMENSIONS[type] = {
    physical,
    clearance: computeClearanceDimensions(type, physical),
  };
}

/**
 * Shower enclosure height — 84". The single source for this number; the
 * placement rules (`designAutomation/rules/showerRules.js`) import it rather
 * than restating it, so the wall a shower is assigned to and the shower that
 * gets built can never disagree.
 */
const SHOWER_ENCLOSURE_HEIGHT_M = 2.134;

/**
 * Minimum WALL height a fixture needs, by type.
 *
 * Only the tall enclosures are constrained. A toilet, sink or tub is
 * floor-standing and short enough that any real wall clears it, and giving
 * them a bound would reject valid layouts for no gain — absent here means
 * "unconstrained", not "zero".
 *
 * A `tub-shower` is a shower over a tub, so it needs the same enclosure height
 * as a shower.
 */
const FIXTURE_MIN_WALL_HEIGHT = {
  shower: SHOWER_ENCLOSURE_HEIGHT_M,
  'tub-shower': SHOWER_ENCLOSURE_HEIGHT_M,
};

/**
 * Get the minimum wall height a fixture needs, in meters. 0 = unconstrained.
 *
 * Without this, S1 assigns fixtures purely in plan and a 1.25 m knee wall looks
 * identical to a 4.7 m one. The downstream ceiling check
 * (`wallFixtureCollision.checkCeilingOverlap`) then vetoes the placement, and
 * because that veto has no fallback the whole fixture is dropped — a bathroom
 * with five good walls silently gets no shower (`66616D52` story-1 bathroom,
 * whose west wall is a 1.25 m knee wall under the shed roof).
 *
 * @param {FixtureType} fixtureType
 * @returns {number} Minimum wall height in meters
 */
function getMinWallHeightForFixture(fixtureType) {
  return FIXTURE_MIN_WALL_HEIGHT[fixtureType] || 0;
}

/**
 * Get minimum wall length needed for a fixture
 * @param {FixtureType} fixtureType
 * @returns {number} Minimum wall length in meters
 */
function getMinWallLengthForFixture(fixtureType) {
  // Use the physical-shrink floor, not the default size, so S1 admits
  // assignments on walls shorter than the default fixture width. S5
  // depth-shrink will size the fixture down if there isn't room for the
  // default. Falls back to FIXTURE_DIMENSIONS.physical for fixture types
  // without a defined min (e.g. composites without a shrink floor).
  const minDims = MIN_PHYSICAL_DIMENSIONS[fixtureType];
  if (minDims) return minDims.width;
  const dims = FIXTURE_DIMENSIONS[fixtureType];
  if (!dims) return 0;
  return dims.physical.width;
}

module.exports = {
  getMinWallLengthForFixture,
  getMinWallHeightForFixture,
  FIXTURE_DIMENSIONS,
  MIN_PHYSICAL_DIMENSIONS,
  RESTORATION_DEPTHS,
  FIXTURE_MIN_WALL_HEIGHT,
  SHOWER_ENCLOSURE_HEIGHT_M,
};
